/**
 * rawFlowApiOf — the raw flow (Standard editor) read/write of one tracker.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Tres Commas flow ids equal Voluum's, so its drawer must never fall back to
 * /voluum/flows: that would load — and on Apply overwrite — the Voluum flow
 * with the same id. Binom has no raw flow (its drawer edits campaigns).
 */

import { matchTracker, type TrackerId } from '@trafficpulse/shared';
import type { VoluumFlowResource, VoluumFlowUpdate } from '@trafficpulse/shared';

import { getRawFlow, updateRawFlow } from '@/features/flow-optimizer/services/voluum-flow-api';

import { getTresCommasRawFlow, updateTresCommasRawFlow } from './tres-commas-raw-flow-api';

export interface RawFlowApi {
  get: (flowId: string) => Promise<VoluumFlowResource>;
  update: (
    flowId: string,
    body: VoluumFlowUpdate,
  ) => Promise<{ success: boolean; flow: VoluumFlowResource }>;
}

export function rawFlowApiOf(provider: TrackerId): RawFlowApi | null {
  return matchTracker<RawFlowApi | null>(provider, {
    voluum: () => ({
      get: (flowId) => getRawFlow(flowId),
      update: (flowId, body) => updateRawFlow(flowId, body),
    }),
    binom: () => null,
    'tres-commas': () => ({
      get: (flowId) => getTresCommasRawFlow(flowId),
      update: (flowId, body) => updateTresCommasRawFlow(flowId, body),
    }),
  });
}

/** Query key of a tracker's raw flow — [tracker, 'flow', 'raw', id]. */
export const rawFlowKey = (provider: TrackerId, flowId: string) =>
  [provider, 'flow', 'raw', flowId] as const;
